import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { bankAccountToFirestore } from "../models/bankAccount";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";

/**
 * BankAccountService
 * Centraliza as operações de banco de dados para o módulo de Contas Bancárias.
 */
const BankAccountService = {
    /**
     * Busca todas as contas de um tenant.
     */
    getBankAccounts: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS),
                orderBy("description", "asc")
            );
            const querySnapshot = await getDocs(q);
            const accounts = [];
            querySnapshot.forEach((doc) => {
                accounts.push({ id: doc.id, ...doc.data() });
            });
            return accounts;
        } catch (error) {
            console.error("Erro ao listar contas bancárias:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma conta bancária.
     */
    save: async (formData, idTenant, authUser, existingAccounts = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const accountData = bankAccountToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const accountRef = doc(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS, formData.id);
                const oldAccount = existingAccounts.find(a => a.id === formData.id) || {};
                await updateDoc(accountRef, accountData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldAccount, accountData);
                if (changes) {
                    AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.BANK_ACCOUNTS,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: formData.id,
                        entityName: accountData.description,
                        description: `Atualizou a conta bancária "${accountData.description}"`,
                        details: changes
                    });
                }

                return { id: formData.id, ...accountData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS), accountData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.BANK_ACCOUNTS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: accountData.description,
                    description: `Criou a conta bancária "${accountData.description}"`
                });

                return { id: newDocRef.id, ...accountData };
            }
        } catch (error) {
            console.error("Erro no BankAccountService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma conta bancária (ou inativa dependendo da regra).
     */
    delete: async (accountId, idTenant, authUser, accountName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS, accountId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.BANK_ACCOUNTS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: accountId,
                entityName: accountName,
                description: `Excluiu a conta bancária "${accountName || accountId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no BankAccountService.delete:", error);
            throw error;
        }
    }
};

export default BankAccountService;
